import React, { useState } from 'react';
import { useLeadIntel } from '../context/LeadIntelContext';
import SourceCard from '../components/lead-intel/SourceCard';
import SignalFeed from '../components/lead-intel/SignalFeed';
import HotLeadsWidget from '../components/lead-intel/HotLeadsWidget';
import SafeIcon from '../common/SafeIcon';
import BaseModal from '../components/modals/BaseModal';
import { AnimatePresence, motion } from 'framer-motion';

const LeadIntelligence = () => {
  const { intentSources, syncJobs, runSync, triggerRules, scoringRules, toggleTrigger, auditLog, alerts } = useLeadIntel();
  const [isConfigModalOpen, setIsConfigModalOpen] = useState(false);

  return (
    <div className="p-8 max-w-[1600px] mx-auto min-h-screen space-y-8 relative">
      {/* Toast Alerts */}
      <div className="fixed top-24 right-8 z-50 flex flex-col gap-2 pointer-events-none">
        <AnimatePresence>
          {alerts.map(alert => (
            <motion.div 
              key={alert.id}
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: 20 }}
              className={`pointer-events-auto p-4 rounded-xl shadow-lg border flex items-center gap-3 min-w-[300px] ${alert.type === 'error' ? 'bg-white border-red-200 text-red-800' : alert.type === 'warning' ? 'bg-white border-amber-200 text-amber-800' : 'bg-slate-900 border-slate-900 text-white'}`}
            >
              <SafeIcon icon={alert.type === 'error' ? "❌" : alert.type === 'warning' ? "⚠️" : "ℹ️"} />
              <p className="text-xs font-bold">{alert.message}</p>
            </motion.div>
          ))}
        </AnimatePresence>
      </div>

      {/* Header */}
      <div className="flex justify-between items-center border-b border-slate-200 pb-6">
        <div>
          <div className="inline-flex items-center gap-2 px-3 py-1 bg-indigo-50 text-indigo-700 rounded-full text-[10px] font-black uppercase tracking-widest mb-2 border border-indigo-100">
            <SafeIcon icon="🧠" className="w-3 h-3" /> Intelligence Engine V1
          </div>
          <h1 className="text-3xl font-black text-slate-900 tracking-tight">Lead Intelligence</h1>
          <p className="text-slate-500 font-medium mt-1">Sanntids-signaler, automatisk scoring og compliance.</p>
        </div>
        <div className="flex gap-3">
          <button 
            onClick={() => setIsConfigModalOpen(true)}
            className="flex items-center gap-2 px-6 py-3 bg-white border border-slate-200 text-slate-700 rounded-xl font-bold text-xs uppercase tracking-widest hover:bg-slate-50 transition-all shadow-sm"
          >
            <SafeIcon icon="⚙️" /> Konfigurasjon
          </button>
        </div>
      </div>

      {/* Stats/Status Bar */}
      <div className="grid grid-cols-4 gap-6">
        <div className="p-4 bg-white rounded-2xl border border-slate-200 shadow-sm flex items-center gap-4">
          <div className="w-10 h-10 bg-blue-50 text-blue-600 rounded-xl flex items-center justify-center"><SafeIcon icon="📡" /></div>
          <div>
            <p className="text-xs text-slate-400 font-bold uppercase">Kilder</p>
            <p className="text-lg font-black text-slate-900">{intentSources.length} Aktive</p>
          </div>
        </div>
        <div className="p-4 bg-white rounded-2xl border border-slate-200 shadow-sm flex items-center gap-4">
          <div className="w-10 h-10 bg-emerald-50 text-emerald-600 rounded-xl flex items-center justify-center"><SafeIcon icon="⚡" /></div>
          <div>
            <p className="text-xs text-slate-400 font-bold uppercase">Triggere</p>
            <p className="text-lg font-black text-slate-900">{triggerRules.filter(t => t.enabled).length} På</p>
          </div>
        </div>
        <div className="p-4 bg-white rounded-2xl border border-slate-200 shadow-sm flex items-center gap-4">
          <div className="w-10 h-10 bg-slate-50 text-slate-600 rounded-xl flex items-center justify-center"><SafeIcon icon="🛡️" /></div>
          <div>
            <p className="text-xs text-slate-400 font-bold uppercase">Compliance</p>
            <p className="text-lg font-black text-slate-900">Safe Mode</p>
          </div>
        </div>
        <div className="p-4 bg-white rounded-2xl border border-slate-200 shadow-sm flex items-center gap-4">
          <div className="w-10 h-10 bg-indigo-50 text-indigo-600 rounded-xl flex items-center justify-center"><SafeIcon icon="🔄" /></div>
          <div>
            <p className="text-xs text-slate-400 font-bold uppercase">Siste Sync</p>
            <p className="text-lg font-black text-slate-900">Nylig</p>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
        {/* Left Column: Signals Feed */}
        <div className="lg:col-span-8 space-y-6">
          <div className="flex justify-between items-center">
            <h3 className="text-lg font-black text-slate-900">Signal Feed</h3>
            <div className="flex gap-2">
              <button className="px-3 py-1 bg-slate-900 text-white rounded-lg text-xs font-bold">Alle</button>
              <button className="px-3 py-1 bg-white border border-slate-200 text-slate-600 rounded-lg text-xs font-bold hover:bg-slate-50">Høy Prio</button>
            </div>
          </div>
          <SignalFeed />
        </div>

        {/* Right Column: Widgets */}
        <div className="lg:col-span-4 space-y-8">
          <HotLeadsWidget />

          {/* Sources */}
          <div>
            <h3 className="text-sm font-black text-slate-400 uppercase tracking-widest mb-4">Datakilder</h3>
            <div className="space-y-4">
              {intentSources.map(source => (
                <SourceCard key={source.source_id} source={source} onSync={runSync} />
              ))}
            </div>
          </div>

          {/* Automations */}
          <div className="bg-slate-50 p-6 rounded-2xl border border-slate-200">
            <h3 className="text-sm font-black text-slate-900 uppercase tracking-widest mb-6 flex items-center gap-2">
              <SafeIcon icon="⚡" className="text-amber-500" /> Aktive Triggere
            </h3>
            <div className="space-y-4">
              {triggerRules.map(rule => (
                <div key={rule.tr_id} className="flex items-center justify-between p-3 bg-white rounded-xl border border-slate-200">
                  <div className="flex-1 mr-2">
                    <p className="text-xs font-bold text-slate-800">{rule.name}</p>
                    <p className="text-[10px] text-slate-400">{rule.action_type} • Cooldown: {rule.cooldown_days}d</p>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input type="checkbox" className="sr-only peer" checked={rule.enabled} onChange={() => toggleTrigger(rule.tr_id)} />
                    <div className="w-9 h-5 bg-slate-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-emerald-500"></div>
                  </label>
                </div>
              ))}
            </div>
          </div>

          {/* Audit Log Tiny View */}
          <div className="bg-slate-900 p-6 rounded-2xl border border-slate-800 text-slate-400">
            <h3 className="text-xs font-black uppercase tracking-widest mb-4 text-slate-500">Audit Log (Siste 5)</h3>
            <div className="space-y-3 font-mono text-[10px]">
              {auditLog.slice(0, 5).map((log, i) => (
                <div key={i} className="flex justify-between">
                  <span className="text-emerald-500">{log.action_type}</span>
                  <span>{new Date(log.occurred_at).toLocaleTimeString()}</span>
                </div>
              ))}
              {auditLog.length === 0 && <p className="text-slate-600">Ingen hendelser logget enda.</p>}
            </div>
          </div>
        </div>
      </div>

      <BaseModal isOpen={isConfigModalOpen} onClose={() => setIsConfigModalOpen(false)} title="Innstillinger Lead Intelligence">
        <div className="p-4 space-y-6">
          <div className="bg-blue-50 p-4 rounded-xl border border-blue-100 flex gap-3">
            <div className="text-blue-600"><SafeIcon icon="ℹ️" /></div>
            <div>
              <h4 className="font-bold text-blue-900 text-sm">Add-on Status: Aktiv</h4>
              <p className="text-xs text-blue-700">Dette er en premium funksjon i ditt abonnement.</p>
            </div>
          </div>
          
          <div className="space-y-4">
            <h4 className="text-xs font-black text-slate-400 uppercase tracking-widest">Scoring Regler</h4>
            <div className="space-y-2">
              {scoringRules.map(rule => (
                <div key={rule.rule_id} className="flex justify-between items-center p-3 bg-slate-50 rounded-xl border border-slate-100">
                  <span className="text-sm font-bold text-slate-700">{rule.name}</span>
                  <span className="text-xs font-black text-emerald-600">+{rule.score_delta} poeng</span>
                </div>
              ))}
            </div>
          </div>

          <div className="bg-yellow-50 p-4 rounded-xl border border-yellow-100 text-xs text-yellow-800">
            <strong>Compliance Note:</strong> Ingen scraping eller automatiserte handlinger mot LinkedIn utføres. Alle data hentes via godkjente API-er. Audit log er aktiv.
          </div>
        </div>
      </BaseModal>
    </div>
  );
};

export default LeadIntelligence;